import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import type { Frame } from '../../domain/entities/Frame';
import type { Cuboid } from '../../domain/entities/Cuboid';
import type { CacheMetrics } from '../../domain/value-objects/CacheMetrics';
import type { TransitionMode } from '../../domain/value-objects/TransitionConfig';
import { InMemoryFrameCache } from '../../infrastructure/cache/InMemoryFrameCache';
import { CDNFrameRepository } from '../../infrastructure/repositories/CDNFrameRepository';
import { parseFrame } from '../../infrastructure/parsers/FrameParser';
import { buildColorBuffer } from '../../domain/services/HeightColorMapper';
import { getWindowFrames } from '../../domain/services/CachePolicy';

interface SceneError {
  frameId: number;
  message: string;
}

interface SceneState {
  currentFrame: Frame | null;
  prevFrame: Frame | null;
  currentFrameId: number;
  isLoading: boolean;
  error: SceneError | null;
  hoveredCuboid: Cuboid | null;
  pinnedCuboid: Cuboid | null;
  cacheMetrics: CacheMetrics;
  prefetchProgress: { loaded: number; total: number } | null;
  transitionMode: TransitionMode;
  cache: InMemoryFrameCache;
}

interface SceneActions {
  navigateTo: (frameId: number) => Promise<void>;
  setHoveredCuboid: (cuboid: Cuboid | null) => void;
  setPinnedCuboid: (cuboid: Cuboid | null) => void;
  setTransitionMode: (mode: TransitionMode) => void;
  prefetchAll: () => Promise<void>;
  clearError: () => void;
}

const SceneStateContext = createContext<SceneState | null>(null);
const SceneActionsContext = createContext<SceneActions | null>(null);

import { TOTAL_FRAMES } from '../../domain/constants';

interface SceneProviderProps {
  maxCacheMb: number;
  debug?: boolean;
  children: React.ReactNode;
}

export function SceneProvider({ maxCacheMb, debug = false, children }: SceneProviderProps) {
  const cacheRef = useRef<InMemoryFrameCache | null>(null);
  if (!cacheRef.current) {
    cacheRef.current = new InMemoryFrameCache(
      maxCacheMb,
      new CDNFrameRepository(),
      parseFrame,
      buildColorBuffer,
      debug,
    );
  }
  const cache = cacheRef.current;

  const [currentFrame, setCurrentFrame] = useState<Frame | null>(null);
  const [prevFrame, setPrevFrame] = useState<Frame | null>(null);
  const [currentFrameId, setCurrentFrameId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SceneError | null>(null);
  const [hoveredCuboid, setHoveredCuboid] = useState<Cuboid | null>(null);
  const [pinnedCuboid, setPinnedCuboid] = useState<Cuboid | null>(null);
  const [cacheMetrics, setCacheMetrics] = useState<CacheMetrics>(cache.getMetrics());
  const [prefetchProgress, setPrefetchProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [transitionMode, setTransitionMode] = useState<TransitionMode>('instant');

  const currentFrameRef = useRef(currentFrame);
  currentFrameRef.current = currentFrame;
  const currentFrameIdRef = useRef(currentFrameId);
  currentFrameIdRef.current = currentFrameId;
  const loadingRef = useRef(false);
  const pendingRef = useRef<number | null>(null);

  const navigateTo = useCallback(async (frameId: number) => {
    const clamped = Math.max(0, Math.min(TOTAL_FRAMES - 1, frameId));
    if (clamped === currentFrameIdRef.current && currentFrameRef.current) return;

    // If a load is in flight, queue the latest request and return
    if (loadingRef.current) {
      pendingRef.current = clamped;
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const frame = await cache.loadFrame(clamped);
      setPrevFrame(currentFrameRef.current);
      setCurrentFrame(frame);
      setCurrentFrameId(clamped);
      currentFrameIdRef.current = clamped;
      currentFrameRef.current = frame;
      setCacheMetrics(cache.getMetrics());

      // Prefetch sliding window (non-blocking)
      const window = getWindowFrames(clamped, TOTAL_FRAMES);
      const windowSet = new Set(window);
      for (const wf of window) {
        if (wf !== clamped) {
          cache.loadFrame(wf, windowSet).then(() => setCacheMetrics(cache.getMetrics())).catch(() => {});
        }
      }
    } catch (err) {
      setError({ frameId: clamped, message: err instanceof Error ? err.message : String(err) });
    } finally {
      loadingRef.current = false;
      setIsLoading(false);

      // Drain pending: if keys were pressed during load, jump to the latest target
      const pending = pendingRef.current;
      if (pending !== null) {
        pendingRef.current = null;
        navigateTo(pending);
      }
    }
  }, [cache]);

  const prefetchAll = useCallback(async () => {
    cache.setEvictionEnabled(false);
    setPrefetchProgress({ loaded: 0, total: TOTAL_FRAMES });
    let loaded = 0;

    for (let i = 0; i < TOTAL_FRAMES; i++) {
      // Skip if already cached
      if (cache.getFrame(i)) {
        loaded++;
        setPrefetchProgress({ loaded, total: TOTAL_FRAMES });
        continue;
      }

      try {
        await cache.loadFrame(i);
        // Check if it was actually cached (putFrame skips when full + no eviction)
        if (!cache.getFrame(i)) {
          break;
        }
        loaded++;
        setPrefetchProgress({ loaded, total: TOTAL_FRAMES });
        setCacheMetrics(cache.getMetrics());
      } catch {
        // Skip failed frames, continue
      }
      // Yield to render loop between frames
      await new Promise(r => setTimeout(r, 0));
    }

    cache.setEvictionEnabled(true);
    setPrefetchProgress(null);
  }, [cache]);

  const clearError = useCallback(() => setError(null), []);

  const state: SceneState = {
    currentFrame,
    prevFrame,
    currentFrameId,
    isLoading,
    error,
    hoveredCuboid,
    pinnedCuboid,
    cacheMetrics,
    prefetchProgress,
    transitionMode,
    cache,
  };

  const actions: SceneActions = {
    navigateTo,
    setHoveredCuboid,
    setPinnedCuboid,
    setTransitionMode,
    prefetchAll,
    clearError,
  };

  return (
    <SceneStateContext.Provider value={state}>
      <SceneActionsContext.Provider value={actions}>
        {children}
      </SceneActionsContext.Provider>
    </SceneStateContext.Provider>
  );
}

export function useSceneState(): SceneState {
  const ctx = useContext(SceneStateContext);
  if (!ctx) throw new Error('useSceneState must be used within SceneProvider');
  return ctx;
}

export function useSceneActions(): SceneActions {
  const ctx = useContext(SceneActionsContext);
  if (!ctx) throw new Error('useSceneActions must be used within SceneProvider');
  return ctx;
}
